<script setup>
// "Your draft" (§5b screen 1; AiStart, AiBookings, AiLimits): the proposal
// filling in live while the conversation runs, with "Things I assumed" (S3,
// each with Change), "Waiting on you" (the open question), "Not possible yet"
// (S4) and "Before you can charge" (S9). Text only, never HTML (SE3).
import {computed} from 'vue';
import {
  canPreviewScoring, draftStepHeading, isRemoveStep, paymentAction, paymentStateWord, sectionCountLine, sectionGroups,
} from '~/utils/setupAssistant';
import ScoringSystemPreviewButton from '~/components/scoring/ScoringSystemPreviewButton.vue';
import SetupStepTile from '~/components/admin/setup/SetupStepTile.vue';
import {useSetupAssistant} from '~/composables/useSetupAssistant';

const props = defineProps({
  proposal: {type: Object, default: null},
  payments: {type: Object, default: null},
  /** The open question, if the assistant is waiting on an answer. */
  question: {type: Object, default: null},
  readOnly: {type: Boolean, default: false},
  busy: {type: Boolean, default: false},
  /** X64: the proposal was applied; the panel is a record and its button opens the done page. */
  applied: {type: Boolean, default: false},
  /** X121 (R55 L1): the applied proposal was undone; the panel is a record of what was reverted, never "live". */
  undone: {type: Boolean, default: false},
});
const emit = defineEmits(['change', 'answer', 'onboard', 'payments']);

const groups = computed(() => sectionGroups(props.proposal));
const steps = computed(() => props.proposal?.steps ?? []);
const assumptions = computed(() => props.proposal?.assumptions ?? []);
const unsupported = computed(() => props.proposal?.unsupported ?? []);
const errors = computed(() => props.proposal?.errors ?? []);
// S9: the club's payment state belongs beside a priced step; a free-only draft has nothing to charge for (R4 Low 4).
const chargeLines = computed(() => {
  const lines = [...(props.proposal?.beforeYouCanCharge ?? [])];
  // X111 (R47 L3): the API's lines quote the payment line after their own words; it is not added a second time.
  if (lines.length && props.payments?.line && !lines.some(l => String(l).includes(props.payments.line))) lines.unshift(props.payments.line);
  return lines;
});
const action = computed(() => paymentAction(props.payments));
const stateWord = computed(() => paymentStateWord(props.payments));
const showCharge = computed(() => chargeLines.value.length > 0 && !['off'].includes(String(props.payments?.nextStep ?? '')));
const statusChip = computed(() => {
  if (props.undone) return 'Undone';
  if (props.applied) return 'Applied';
  if (!steps.value.length) return 'Nothing created yet';
  return props.proposal?.ready ? 'Ready to apply' : 'Nothing created yet';
});
// The draft's button (Review and apply / Check the draft / See what changed) is SetupDraftFoot, pinned under this
// scrolling panel by the dock (Grant 2026-11-03: it was hidden below the scroll).
/** X122 (R57 L2): an applied or undone session has ended, so nothing here asks for an answer or a change. */
const ended = computed(() => props.applied || props.undone);
// X200: a drafted scoring system opens the same "Preview score input" dialog as the check page.
const {scoringPreview} = useSetupAssistant();
const previewLoader = (s) => () => scoringPreview(props.proposal?.id, s.id);
// Grant 2026-11-03 (as the app's draft sheet): each step is its own card, led by its identity tile (a competition's format
// icon in its colour, the admin menu's icon on the neutral tile otherwise), "New box league" as its heading, the name under it.
const stepName = (s) => String(s?.label ?? '').trim();
</script>

<template>
  <div class="tl-card sdp">
    <div class="sdp-head">
      <div class="sdp-title">Your draft</div>
      <span class="sdp-head-end">
        <span class="tl-chip" :class="applied || proposal?.ready ? 'tl-chip--ok' : ''">{{ statusChip }}</span>
        <!-- X178: the floating draft's collapse control -->
        <slot name="head-end"/>
      </span>
    </div>

    <div v-if="!steps.length" class="sdp-empty">
      As you answer, the playing areas, competitions, memberships and booking rules it drafts appear here. Nothing is created until you check it.
    </div>

    <div v-for="g in groups" :key="g.key" class="sdp-section">
      <div class="sdp-sec-t">{{ sectionCountLine(g) }}</div>
      <div v-for="s in g.steps" :key="s.id" class="sdp-step" :class="{'sdp-step--err': s.errors?.length}" :data-testid="`setup-draft-step-${s.id}`">
        <div class="sdp-step-row">
          <setup-step-tile :step="s"/>
          <div class="sdp-step-words">
            <div class="sdp-step-t" :class="{'sdp-step-t--remove': isRemoveStep(s)}">{{ draftStepHeading(s) }}</div>
            <div v-if="stepName(s)" class="sdp-step-n">{{ stepName(s) }}</div>
          </div>
        </div>
        <div v-if="s.summary" class="sdp-step-d">{{ s.summary }}</div>
        <scoring-system-preview-button v-if="proposal?.id && canPreviewScoring(s)" class="sdp-step-preview" :load="previewLoader(s)"/>
        <div v-for="(e, i) in s.errors ?? []" :key="i" class="sdp-step-e">{{ e }}</div>
      </div>
    </div>

    <div v-if="errors.length" class="sdp-section">
      <div class="sdp-sec-t sdp-sec-t--warn">Needs fixing · {{ errors.length }}</div>
      <ul class="sdp-list">
        <li v-for="(e, i) in errors" :key="i">{{ e }}</li>
      </ul>
    </div>

    <div v-if="assumptions.length" class="sdp-section">
      <div class="sdp-sec-t">Things I assumed · {{ assumptions.length }}</div>
      <ul class="sdp-list">
        <li v-for="(a, i) in assumptions" :key="i" class="sdp-assume">
          <span class="sdp-assume-t">{{ a.text }}</span>
          <button v-if="!readOnly && !ended" type="button" class="sdp-link" :disabled="busy" @click="emit('change', a)">Change</button>
        </li>
      </ul>
    </div>

    <div v-if="question && !ended" class="sdp-section">
      <div class="sdp-sec-t">Waiting on you · 1</div>
      <ul class="sdp-list">
        <li class="sdp-assume">
          <span class="sdp-assume-t">{{ question.text }}</span>
          <button type="button" class="sdp-link" @click="emit('answer', question)">Answer</button>
        </li>
      </ul>
    </div>

    <div v-if="unsupported.length" class="sdp-section">
      <div class="sdp-sec-t">Not possible yet · {{ unsupported.length }}</div>
      <ul class="sdp-list">
        <li v-for="(u, i) in unsupported" :key="i">
          {{ u.request }} (passed to Tap Leagues).{{ u.closestOption ? ' ' + u.closestOption : '' }}
        </li>
      </ul>
    </div>

    <div v-if="showCharge" class="sdp-charge">
      <div class="sdp-charge-h">
        <v-icon size="16" color="warning">mdi-credit-card-clock-outline</v-icon>
        <span>Before you can charge</span>
        <span v-if="stateWord" class="tl-chip sdp-charge-state">{{ stateWord }}</span>
      </div>
      <p v-for="(l, i) in chargeLines" :key="i" class="sdp-charge-p">{{ l }}</p>
      <p v-if="payments?.feeLabel && payments?.nextStep === 'ready'" class="sdp-charge-p">Fees: {{ payments.feeLabel }}<template v-if="payments.commissionPercent != null"> plus Tap Leagues {{ payments.commissionPercent }}%</template>. Members pay the price you set.</p>
      <v-btn
          v-if="action && !readOnly"
          size="small"
          :color="action.kind === 'onboard' ? 'primary' : undefined"
          :variant="action.kind === 'onboard' ? 'flat' : 'outlined'"
          class="mt-2"
          @click="emit(action.kind === 'onboard' ? 'onboard' : 'payments')"
      >{{ action.label }}</v-btn>
      <div v-else-if="action && readOnly" class="sdp-charge-ro">Only a club admin can {{ action.label.toLowerCase() }}.</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.sdp { padding: 16px; }

.sdp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.sdp-head-end { display: inline-flex; align-items: center; gap: 4px; }

.sdp-title { font-size: 15px; font-weight: 800; letter-spacing: -0.2px; }

.sdp-empty {
  font-size: 13px;
  line-height: 1.55;
  color: rgb(var(--v-theme-textSubtle));
  padding: 6px 0 4px;
}

.sdp-section { margin-top: 14px; }

.sdp-sec-t {
  font-size: 11.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: rgb(var(--v-theme-textSubtle));
  margin-bottom: 6px;

  &--warn { color: rgb(var(--v-theme-error)); }
}

.sdp-step {
  padding: 8px 10px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  margin-bottom: 6px;

  &--err { border-color: rgba(var(--v-theme-error), 0.45); }
}

.sdp-step-row { display: flex; align-items: center; gap: 10px; min-width: 0; }
.sdp-step-words { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.sdp-step-t { font-size: 13.5px; font-weight: 700; word-break: break-word; }
.sdp-step-t--remove { color: rgb(var(--v-theme-error)); }
.sdp-step-n { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); word-break: break-word; }
.sdp-step-d { font-size: 12.5px; color: rgb(var(--v-theme-charcoal)); line-height: 1.5; margin-top: 6px; white-space: pre-wrap; word-break: break-word; }
.sdp-step-preview { display: inline-block; margin: 2px 0 0 -8px; }
.sdp-step-e { font-size: 12px; color: rgb(var(--v-theme-error)); margin-top: 3px; }

.sdp-list {
  margin: 0;
  padding-left: 18px;
  font-size: 12.5px;
  line-height: 1.55;
  color: rgb(var(--v-theme-charcoal));

  li { margin-bottom: 4px; word-break: break-word; }
}

.sdp-assume { display: flex; align-items: flex-start; gap: 8px; }
.sdp-assume-t { flex: 1; min-width: 0; white-space: pre-wrap; }

.sdp-link {
  background: none;
  border: 0;
  padding: 0;
  color: rgb(var(--v-theme-primary));
  font-weight: 600;
  font-size: 12.5px;
  cursor: pointer;
  flex-shrink: 0;

  &:disabled { opacity: 0.5; cursor: default; }
}

.sdp-charge {
  margin-top: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(var(--v-theme-warning), 0.08);
  border: 1px solid rgba(var(--v-theme-warning), 0.35);
}

.sdp-charge-h { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 700; flex-wrap: wrap; }
.sdp-charge-state { font-size: 10.5px; }
.sdp-charge-p { font-size: 12.5px; line-height: 1.5; margin: 6px 0 0; white-space: pre-wrap; }
.sdp-charge-ro { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 6px; }
</style>
